<script setup lang="ts">
import { useUserStore } from '@/stores/user'
import { Field, useForm } from 'vee-validate'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

const userStore = useUserStore()
const { t } = useI18n()
const toaster = useToaster()
const router = useRouter()
const route = useRoute()
const runtimeConfig = useRuntimeConfig()

const initialValues = ref({})

const { handleSubmit, isSubmitting, setFieldValue, errors } = useForm({
  initialValues,
})

const success = ref(false)

const acceptTerms = ref(false)
const userAggrement = ref(false)

const kvkkUrl =
  runtimeConfig.public.source == 'user'
    ? '/pages/Gigcep-KVKK-Aydinlatma-Metni'
    : '/pages/360Sosyal-KVKK-Aydinlatma-Metni'
const userContractUrl =
  runtimeConfig.public.source == 'user'
    ? '/pages/Gigcep-Kullanici-Sozlesmesi'
    : '/pages/360Sosyal-Kullanici-Sozlesmesi'

const userAggrementUrl =
  runtimeConfig.public.source == 'user'
    ? '/pages/Gigcep-Acik-Riza-Metni'
    : '/pages/360Sosyal-Acik-Riza-Metni'

const onSubmit = handleSubmit(async (values) => {
  try {
    success.value = false
    const body = {
      ...values,
    }

    if (route.query.reference) {
      body.reference = route.query.reference
    }
    if (runtimeConfig.public.source == 'customer') {
      body.gender = 'N'
    }

    const { data, error } = await useCustomFetch('/api/register', {
      method: 'post',
      body: body,
    })

    if (!data.value?.success) {
      useHandleError(data.value)
      return
    } else {
      if (data.value.message === 'VERIFICATION_REQUIRED') {
        userStore.setRegisterToken(data.value.data.register_token)
        userStore.setPhone(values.phone)
        userStore.setVerifyRequiredFields({
          mobile_verification_required:
            data.value.data.mobile_verification_required == '1',
          email_verification_required:
            data.value.data.email_verification_required == '1',
        })
        router.push('/verify')
      } else {
        userStore.setToken(data.value.data.access_token)
        toaster.show({
          title: t('success'),
          message: data.value.message,
          color: 'success',
          icon: 'ph:check',
          closable: true,
        })
        router.push('/home')
      }
    }

    // resetForm()
  } catch (error) {
    console.error(error)
  }
})
</script>

<template>
  <div class="grid gap-1 lg:gap-2 grid-cols-1 md:grid-cols-2">
    <Field
      v-slot="{ field, errorMessage, handleChange, handleBlur }"
      name="name"
      rules="required"
    >
      <BaseInput
        :label="$t('name')"
        icon="ic:baseline-person"
        :model-value="field.value"
        :error="errorMessage"
        :disabled="isSubmitting"
        type="text"
        @update:model-value="handleChange"
        @blur="handleBlur"
      />
    </Field>

    <Field
      v-slot="{ field, errorMessage, handleChange, handleBlur }"
      name="surname"
      rules="required"
    >
      <BaseInput
        :label="$t('surname')"
        icon="ic:baseline-person"
        :model-value="field.value"
        :error="errorMessage"
        :disabled="isSubmitting"
        type="text"
        @update:model-value="handleChange"
        @blur="handleBlur"
      />
    </Field>

    <Field
      v-slot="{ field, errorMessage, handleChange, handleBlur }"
      name="email"
      rules="required|email"
    >
      <BaseInput
        :label="$t('email')"
        icon="material-symbols:mail-outline"
        :model-value="field.value"
        :error="errorMessage"
        :disabled="isSubmitting"
        type="text"
        @update:model-value="handleChange"
        @blur="handleBlur"
      />
    </Field>
    <Field
      v-if="runtimeConfig.public.source == 'user'"
      v-slot="{ field, errorMessage, handleChange, handleBlur }"
      name="gender"
    >
      <SelectGender
        :label="$t('gender')"
        :disabled="isSubmitting"
        :error="errorMessage"
        :model-value="field.value"
        @update:modelValue="(e: string) => setFieldValue('gender', e)"
        :allVisible="false"
      />
    </Field>
    <Field
      v-slot="{ field, errorMessage, handleChange, handleBlur }"
      name="phone"
      rules="required|numeric|length:10"
    >
      <BaseInput
        :label="$t('phone')"
        icon="ic:baseline-phone"
        :model-value="field.value"
        :error="errorMessage"
        :disabled="isSubmitting"
        type="text"
        @update:model-value="handleChange"
        @blur="handleBlur"
      />
    </Field>
    <Field
      v-slot="{ field, errorMessage, handleChange, handleBlur }"
      name="password"
      rules="required"
    >
      <BaseInput
        :label="$t('password')"
        icon="material-symbols:lock"
        :model-value="field.value"
        :error="errorMessage"
        :disabled="isSubmitting"
        type="password"
        @update:model-value="handleChange"
        @blur="handleBlur"
      />
    </Field>
    <Field
      v-slot="{ field, errorMessage, handleChange, handleBlur }"
      name="password_confirmation"
      rules="required|confirmed:@password"
    >
      <BaseInput
        :label="$t('password_confirmation')"
        icon="material-symbols:lock"
        :model-value="field.value"
        :error="errorMessage"
        :disabled="isSubmitting"
        type="password"
        @update:model-value="handleChange"
        @blur="handleBlur"
      />
    </Field>
    <Field
      v-slot="{ field, errorMessage, handleChange, handleBlur }"
      name="kvkk"
    >
      <!-- <BaseCheckbox
        :model-value="field.value"
        :disabled="isSubmitting"
        shape="rounded"
        :label="$t('remember_this_account')"
        color="primary"
        @update:model-value="handleChange"
        @blur="handleBlur"
        @keyup.enter="onSubmit"
      /> -->
    </Field>
  </div>
  <div class="grid gap-1 lg:gap-2 grid-cols-1 mt-6">
    <BaseCheckbox v-model="acceptTerms" id="accept-terms">
      <template #default>
        <NuxtLink
          target="_blank"
          :to="userContractUrl"
          class="!font-bold text-slate-900"
        >
          Kullanıcı Sözleşmesi
        </NuxtLink>
        ’ni ve
        <NuxtLink
          target="_blank"
          :to="kvkkUrl"
          class="!font-bold text-slate-900"
        >
          KVKK Aydınlatma Metni
        </NuxtLink>
        ’ni okudum, anladım ve kabul ediyorum.
      </template>
    </BaseCheckbox>
    <BaseCheckbox v-model="userAggrement" id="user-aggrement">
      <template #default>
        <NuxtLink
          target="_blank"
          :to="userAggrementUrl"
          class="!font-bold text-slate-900"
        >
          Açık Rıza Beyanı
        </NuxtLink>
        ’nı okudum, anladım ve bu kapsamda kişisel verilerimin işlenmesi ve
        aktarılmasını onaylıyorum.
      </template>
    </BaseCheckbox>
  </div>
  <div class="text-center mt-10">
    <BaseButton
      class="w-full md:w-1/2 h-10 text-base"
      @click="onSubmit"
      rounded="md"
      color="primary"
      :disabled="isSubmitting || !acceptTerms || !userAggrement"
      :loading="isSubmitting"
    >
      {{ $t('sign_in') }}
    </BaseButton>
  </div>
</template>
